import { useQuery } from '@tanstack/react-query';

import { usePageVisible } from '@/lib/hooks/usePageVisible';
import { limitConcurrency } from '@/lib/limitConcurrency';
import { fetchCandles } from '@/lib/tv/hlDatafeed';

import type { Candle } from './levels';

const DAY_MS = 24 * 60 * 60 * 1000;
/** Three days of 15-minute bars: 288 of them, one request. */
export const LOOKBACK_MS = 3 * DAY_MS;
export const INTERVAL = '15m';
/**
 * A 15-minute bar changes the swing picture at most every 15 minutes; five
 * is a compromise between "the newest bar is in" and not re-fetching for a
 * picture that has not moved.
 */
const REFETCH_MS = 5 * 60 * 1000;

/**
 * The same lane discipline as the sparklines: this request competes with the
 * chart's own candle fetches on the same venue, and the venue answers a burst
 * with 429s. Module-scoped so the limit is over the app, not per hook.
 */
const CANDLES_LANE = limitConcurrency(2);

export interface CandlesState {
    bars: Candle[];
    /** when the bars were last fetched; drives the "updated N min ago" line */
    updatedAt: number;
    isLoading: boolean;
    isError: boolean;
}

const NO_BARS: Candle[] = [];

/**
 * Three days of 15m candles for one coin, straight from Hyperliquid, on the
 * user's own IP — which is the point: nothing here touches the Backend or the
 * rate budget the trade reconciler lives in. Fetched only while enabled and
 * the page is visible, and never polled while the feature is off.
 */
export function useCandles(coin: string | null, enabled: boolean): CandlesState {
    const visible = usePageVisible();
    const active = enabled && visible && !!coin;

    const query = useQuery({
        queryKey: ['hl-candles', coin, INTERVAL, LOOKBACK_MS],
        enabled: active,
        staleTime: REFETCH_MS,
        refetchInterval: active ? REFETCH_MS : false,
        retry: 1,
        queryFn: async ({ signal }) =>
            CANDLES_LANE(async () => {
                if (signal.aborted) throw signal.reason;
                const end = Date.now();
                const bars = await fetchCandles(
                    coin!,
                    INTERVAL,
                    end - LOOKBACK_MS,
                    end,
                    signal,
                );
                return bars
                    .filter(
                        (b) =>
                            Number.isFinite(b.high) &&
                            Number.isFinite(b.low) &&
                            Number.isFinite(b.close) &&
                            b.low > 0,
                    )
                    .map<Candle>((b) => ({
                        time: b.time,
                        high: b.high,
                        low: b.low,
                        close: b.close,
                    }));
            }),
    });

    return {
        bars: query.data ?? NO_BARS,
        updatedAt: query.dataUpdatedAt,
        isLoading: query.isLoading,
        isError: query.isError,
    };
}
